import { supabase } from '@/integrations/supabase/client';
import { enrollStudentInActivity, EnrollmentData } from './useParentEnrollment';

export interface EnrollmentWithScheduleData extends EnrollmentData {
  selectedDays?: number[]; // Días específicos para actividades que requieren selección
}

interface EnrollmentWithScheduleResult {
  student: any;
  enrollment: any;
  schedule?: any[];
  isNewStudent: boolean;
  message: string;
}

// Crear horario específico para un estudiante en una actividad
export const createStudentSchedule = async (
  studentId: string, 
  activityId: string, 
  selectedDays: number[]
) => {
  console.log('📅 Creando horario para estudiante:', { studentId, activityId, selectedDays });

  const scheduleData = selectedDays.map(day => ({
    student_id: studentId,
    activity_id: activityId,
    day_of_week: day
  }));

  const { data: schedule, error } = await supabase
    .from('student_activity_schedule')
    .insert(scheduleData)
    .select();

  if (error) throw error;

  console.log('✅ Horario creado:', schedule);
  return schedule;
};

// Función principal para inscribir estudiante con programación de días
export const enrollStudentWithSchedule = async (
  data: EnrollmentWithScheduleData
): Promise<EnrollmentWithScheduleResult> => {
  try {
    console.log('🚀 Iniciando inscripción con programación:', data);

    // 1. Realizar inscripción normal
    const enrollmentResult = await enrollStudentInActivity(data);

    // 2. Si hay días específicos seleccionados, crear el horario
    let schedule = null;
    if (data.selectedDays && data.selectedDays.length > 0) {
      schedule = await createStudentSchedule(
        enrollmentResult.student.id,
        data.activityId,
        data.selectedDays
      );
      
      console.log('✅ Inscripción con horario específico completada');
    } else {
      console.log('✅ Inscripción normal completada (todos los días)');
    }

    return {
      ...enrollmentResult,
      schedule,
      message: schedule 
        ? `${enrollmentResult.student.first_name} ${enrollmentResult.student.last_name} ha sido inscrito/a con horario específico en la actividad`
        : enrollmentResult.message
    };

  } catch (error: any) {
    console.error('❌ Error en inscripción con programación:', error);
    throw new Error(error.message || 'Error desconocido en el proceso de inscripción');
  }
};

// Obtener días programados para un estudiante en una actividad
export const getStudentSchedule = async (studentId: string, activityId: string) => {
  const { data: schedule, error } = await supabase
    .from('student_activity_schedule')
    .select('day_of_week')
    .eq('student_id', studentId)
    .eq('activity_id', activityId)
    .order('day_of_week');

  if (error) throw error;

  return schedule?.map(s => s.day_of_week) || [];
};

// Actualizar horario de un estudiante
export const updateStudentSchedule = async (
  studentId: string,
  activityId: string,
  newDays: number[]
) => {
  console.log('📝 Actualizando horario:', { studentId, activityId, newDays });

  // Eliminar horario existente
  const { error: deleteError } = await supabase
    .from('student_activity_schedule')
    .delete()
    .eq('student_id', studentId)
    .eq('activity_id', activityId);

  if (deleteError) throw deleteError;

  // Crear nuevo horario si hay días seleccionados
  if (newDays.length > 0) {
    const schedule = await createStudentSchedule(studentId, activityId, newDays);
    return schedule;
  }

  return [];
};

// Obtener estudiantes que deberían estar presente en un día específico
export const getStudentsForDay = async (activityId: string, dayOfWeek: number) => {
  console.log('📋 Obteniendo estudiantes para el día:', { activityId, dayOfWeek });

  // Primero, verificar si la actividad requiere selección de días
  const { data: activity, error: activityError } = await supabase
    .from('activities')
    .select('requires_day_selection')
    .eq('id', activityId)
    .single();

  if (activityError) throw activityError;

  if (activity.requires_day_selection) {
    // Para actividades con selección de días, obtener solo los programados para este día
    const { data: scheduledStudents, error } = await supabase
      .from('student_activity_schedule')
      .select(`
        student_id,
        students!inner (
          id,
          name,
          first_name,
          last_name,
          class,
          email,
          phone
        )
      `)
      .eq('activity_id', activityId)
      .eq('day_of_week', dayOfWeek);

    if (error) throw error;

    return scheduledStudents?.map(s => s.students) || [];
  } else {
    // Para actividades normales, obtener todos los estudiantes inscritos
    const { data: allStudents, error } = await supabase
      .from('student_activities')
      .select(`
        students!inner (
          id,
          name,
          first_name,
          last_name,
          class,
          email,
          phone
        )
      `)
      .eq('activity_id', activityId)
      .eq('status', 'active');

    if (error) throw error;

    return allStudents?.map(s => s.students) || [];
  }
};